<template>
  <div class="flex flex-col items-center">
    <!-- Field Label -->
    <label :for="item.field" class="block relative text-sm font-medium uppercase text-primary-800 mb-4">
      <span v-if="item.required" class="inline mr-1 text-sm text-red-400">*</span>
      {{ item.name }}
    </label>

    <!-- Image Display -->
    <div class="w-full max-w-md bg-white rounded-md shadow-sm p-4 mb-4 flex flex-col items-center">
      <!-- Upload Progress -->
      <div v-if="sending" class="w-full max-w-md mt-4 text-center">
        <TgLoader />
        <div class="mt-2 text-gray-600">
          Uploading {{ complete }} of {{ total }} files...
        </div>
      </div>
      <template v-else>
        <template v-if="props.modelValue">
          <template v-if="!imgError">
            <img class="mx-auto" :src="imageUrl" @error="handleImgError">
          </template>
          <template v-else>
            <svg
              xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
              stroke="currentColor" class="w-6 h-6"
            >
              <path
                stroke-linecap="round" stroke-linejoin="round"
                d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
              />
            </svg>
          </template>
          <div class="mt-2 flex space-x-4">
            <button type="button" class="text-sm text-red-400" @click="del">
              Remove
            </button>
            <button type="button" class="text-sm text-primary-600" @click="triggerFilePickerAndReset">
              Replace
            </button>
          </div>
        </template>
        <template v-else>
          <div class="flex flex-col items-center cursor-pointer" @click="triggerFilePickerAndReset">
            <svg
              xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
              stroke="currentColor" class="w-12 h-12 text-gray-400"
            >
              <path
                stroke-linecap="round" stroke-linejoin="round"
                d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
              />
            </svg>
            <div class="mt-2 text-gray-600">
              Click to add an image
            </div>
          </div>
        </template>
      </template>
    </div>

    <!-- Hidden File Input for Uploader -->
    <input
      id="file-upload" ref="fileRef" type="file" multiple accept=".pdf,.jpg,.jpeg,.png" class="hidden"
      @change="onChange"
    >
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import TgLoader from '@/components/Common/TgLoader.vue'
import _head from 'lodash/head'
import { API } from '@/api'

const props = defineProps({
  item: Object,
  filename: String,
  folder: String,
  slug: String,
  error: String,
  modelValue: {
    type: [String, Number, Boolean],
    default: '',
  }
})
const emit = defineEmits(['update:modelValue', 'update:error'])

const imgError = ref(false)

const imageUrl = computed(() => {
  if (!props.modelValue) return ''
  return props.modelValue.startsWith('http')
    ? props.modelValue
    : `https://assets.touchpointglobal.com/${props.modelValue}`
})


const route = useRoute()
const clientKey = computed(() => route.params.client)

const fileRef = ref(null)
const previewFiles = ref([])
const complete = ref(0)
const total = ref(0)
const sending = ref(false)

function onChange(event) {
  const files = event.target.files
  if (files.length) {
    const fileArray = Array.from(files)
    previewFiles.value = fileArray
    total.value = fileArray.length
    complete.value = 0
    sending.value = true

    Promise.all(fileArray.map((file, index) => get_secure_upload_url(file, index)))
      .then(() => {
        sending.value = false
        if (fileRef.value) fileRef.value.value = ''
        previewFiles.value = []
      })
      .catch(err => {
        console.error('Error uploading files:', err)
        sending.value = false
      })
  }
}

async function get_secure_upload_url(file, index) {
  previewFiles.value[index] = {
    name: file.name,
    type: file.type,
    status: "Getting secure URL"
  }

  const isTouchpointAssets = true // Change this if you want dynamic switching

  const params = {
    action: "put",
    folder: props.folder,
    filekey: `${route.params.client}/${API.assets_folder}/${props.folder}/${file.name}`,
    bucket: isTouchpointAssets ? 'assets.touchpointglobal.com' : 'workers.tplive.au',
    pub: 0,
    filetype: file.type,
  }

  try {
    const resp = await API.get_secure_upload_url(props.slug, file.name, params)

    const { url, key, link } = resp.data.data

    await upload(file, url)

    // Use the link provided by API if available, fallback to known path
    const publicUrl = link || (
      params.bucket === 'assets.touchpointglobal.com'
        ? `https://assets.touchpointglobal.com/${key}`
        : `https://s3.ap-southeast-2.amazonaws.com/${params.bucket}/${key}`
    )

    console.log("✅ Uploaded:", publicUrl)
    imgError.value = false;
    emit("update:modelValue", publicUrl)
    complete.value++
  } catch (err) {
    console.error("Upload failed:", err)
    previewFiles.value[index].status = "Failed"
    emit("update:error", 'Failed to upload')
  }
}

async function upload(file, signedUrl) {
  return await fetch(signedUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type,
    },
  })
}

function del() {
  emit('update:modelValue', '')
}

function handleImgError() {
  imgError.value = true
}

function triggerFilePicker() {
  if (fileRef.value) {
    fileRef.value.click()
  }
}

function triggerFilePickerAndReset() {
  imgError.value = false
  triggerFilePicker()
}
</script>



<style scoped>
.uploader-container {
  max-width: 500px;
  margin: 0 auto;
}

.file-upload-label {
  font-size: 1rem;
  color: #4a5568;
}

.preview-area {
  font-size: 0.9rem;
}

.preview-item {
  background-color: #f7fafc;
}
</style>